"use client"

import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { apiClient } from "@/lib/api-client"
import {
  activeAssignments,
  assignmentStateReducer,
  initialAssignmentState,
  type AccessAssignment,
  type OrganizationAppAccess,
} from "@/lib/access-control"
import { useAuth } from "@/contexts/auth-context"

type OrganizationOption = { id: string; name: string }
type PlatformOrganizationRole = 'owner' | 'super_admin' | null

type OrganizationContextValue = {
  organizationId: string | null
  organizations: OrganizationOption[]
  assignments: AccessAssignment[]
  appAccess: OrganizationAppAccess[]
  isReady: boolean
  isLoading: boolean
  organizationError: string | null
  selectOrganization: (organizationId: string | null) => void
  refreshAssignments: () => Promise<void>
}

const OrganizationContext = createContext<OrganizationContextValue | undefined>(undefined)

function storageKey(userId: string): string {
  return `rbac.organization.${userId}`
}

export function isPlatformOrganizationRole(role: PlatformOrganizationRole): boolean {
  return role === 'owner' || role === 'super_admin'
}

export function isOrganizationDataCurrent(
  loadedIdentityKey: string | null,
  currentIdentityKey: string,
): boolean {
  return loadedIdentityKey === currentIdentityKey
}

export function resolveInitialOrganizationId(
  platformRole: PlatformOrganizationRole,
  stored: string | null,
  options: OrganizationOption[],
): string | null {
  if (isPlatformOrganizationRole(platformRole)) return null
  return stored && options.some((organization) => organization.id === stored)
    ? stored
    : options[0]?.id ?? null
}

export function resolveAssignmentOrganizationId(
  platformRole: PlatformOrganizationRole,
  contextOrganizationId: string | null,
  explicitOrganizationId: string | null,
): string | null {
  return isPlatformOrganizationRole(platformRole) ? explicitOrganizationId : contextOrganizationId
}

export function OrganizationProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth()
  const platformRole = session?.platform_role ?? null
  const identityKey = `${session?.user?.id ?? "anonymous"}:${platformRole ?? "scoped"}`
  const identityKeyRef = useRef(identityKey)
  identityKeyRef.current = identityKey
  const [state, dispatch] = useReducer(assignmentStateReducer, initialAssignmentState)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [appAccess, setAppAccess] = useState<OrganizationAppAccess[]>([])
  const [loadedIdentityKey, setLoadedIdentityKey] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [organizationError, setOrganizationError] = useState<string | null>(null)

  const selectOrganization = useCallback((organizationId: string | null) => {
    if (isPlatformOrganizationRole(session?.platform_role ?? null)) return
    apiClient.setActiveOrganizationId(organizationId)
    dispatch({ type: "organization-selected", organizationId })
    const userId = session?.user?.id
    if (!userId || typeof window === "undefined") return
    if (organizationId) window.localStorage.setItem(storageKey(userId), organizationId)
    else window.localStorage.removeItem(storageKey(userId))
  }, [session?.user?.id, platformRole])

  const refreshAssignments = useCallback(async () => {
    if (isPlatformOrganizationRole(platformRole) || loadedIdentityKey !== identityKey || !state.organizationId) return
    const requestIdentityKey = identityKey
    const organizationId = state.organizationId
    setIsLoading(true)
    try {
      const { assignments } = await apiClient.listAccessAssignments(organizationId)
      if (identityKeyRef.current !== requestIdentityKey) return
      dispatch({ type: "loaded", organizationId, assignments })
    } finally {
      if (identityKeyRef.current === requestIdentityKey) setIsLoading(false)
    }
  }, [identityKey, loadedIdentityKey, platformRole, state.organizationId])

  useEffect(() => {
    const userId = session?.user?.id
    if (!userId) {
      apiClient.setActiveOrganizationId(null)
      setOrganizations([])
      setAppAccess([])
      setLoadedIdentityKey(null)
      setOrganizationError(null)
      setIsLoading(false)
      dispatch({ type: "organization-selected", organizationId: null })
      return
    }

    apiClient.setActiveOrganizationId(null)
    setOrganizations([])
    setAppAccess([])
    setLoadedIdentityKey(null)
    setOrganizationError(null)
    setIsLoading(true)
    dispatch({ type: "organization-selected", organizationId: null })
    let cancelled = false
    void (async () => {
      const discovered = new Map<string, string>()
      let discoveryError: string | null = null
      let discoveredAppAccess: OrganizationAppAccess[] = []
      if (!isPlatformOrganizationRole(platformRole)) {
        try {
          const { user_apps } = await apiClient.getUserApps({ user_id: userId })
          discoveredAppAccess = user_apps
          for (const assignment of user_apps) {
            if (assignment.organization_id && assignment.is_active) {
              discovered.set(
                assignment.organization_id,
                assignment.organization_name || `Organización ${assignment.organization_id.slice(0, 8)}`,
              )
            }
          }
        } catch (error) {
          console.error("Failed to discover organization access:", error)
          discoveryError = "No se pudieron cargar las organizaciones para esta sesión."
        }
      }

      if (isPlatformOrganizationRole(platformRole)) {
        try {
          const { organizations: rows } = await apiClient.getOrganizations()
          for (const organization of rows) discovered.set(organization.id, organization.name)
        } catch (error) {
          console.error("Failed to load organization catalog:", error)
          discoveryError = "No se pudieron cargar las organizaciones para esta sesión."
        }
      }

      if (cancelled) return
      const options = Array.from(discovered, ([id, name]) => ({ id, name }))
      if (identityKeyRef.current !== identityKey) return
      setOrganizations(options)
      setAppAccess(discoveredAppAccess)
      setLoadedIdentityKey(identityKey)
      setOrganizationError(discoveryError)
      setIsLoading(false)
      const stored = typeof window === "undefined" ? null : window.localStorage.getItem(storageKey(userId))
      const selected = resolveInitialOrganizationId(platformRole, stored, options)
      apiClient.setActiveOrganizationId(selected)
      dispatch({ type: "organization-selected", organizationId: selected })
    })()

    return () => { cancelled = true }
  }, [identityKey, platformRole, session?.user?.id])

  useEffect(() => {
    void refreshAssignments().catch((error) => {
      console.error("Failed to refresh organization assignments:", error)
    })
  }, [refreshAssignments])

  const readyForCurrentIdentity = isOrganizationDataCurrent(loadedIdentityKey, identityKey)
  const visibleOrganizationId = isPlatformOrganizationRole(platformRole) || !readyForCurrentIdentity
    ? null
    : state.organizationId
  const value = useMemo<OrganizationContextValue>(() => ({
    organizationId: visibleOrganizationId,
    organizations: readyForCurrentIdentity ? organizations : [],
    appAccess: readyForCurrentIdentity ? appAccess : [],
    isReady: readyForCurrentIdentity,
    assignments: isPlatformOrganizationRole(platformRole) || !readyForCurrentIdentity
      ? []
      : activeAssignments(state.assignments),
    isLoading,
    organizationError,
    selectOrganization,
    refreshAssignments,
  }), [appAccess, isLoading, organizationError, organizations, platformRole, readyForCurrentIdentity, refreshAssignments, selectOrganization, state.assignments, visibleOrganizationId])

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>
}

export function useOrganization(): OrganizationContextValue {
  const context = useContext(OrganizationContext)
  if (!context) throw new Error("useOrganization must be used within an OrganizationProvider")
  return context
}
